import { RiSidebarFoldLine, RiSidebarUnfoldLine } from "@remixicon/react";
import { Button } from "@screener/uikit/components/button";
import { Logo } from "@screener/uikit/components/logo";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@screener/uikit/components/sheet";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarSeparator,
  SidebarTrigger,
  useSidebar,
} from "@screener/uikit/components/sidebar";
import { Tooltip, TooltipContent, TooltipTrigger } from "@screener/uikit/components/tooltip";
import type { ThemeChoice } from "@screener/uikit/hooks/use-theme";
import { cn } from "@screener/uikit/lib/utils";
import {
  type ComponentProps,
  type CSSProperties,
  type RefObject,
  useCallback,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { AlertStatus } from "@/alerts/alert-status";
import type { UseAlertActionsResult } from "@/alerts/use-alert-actions";
import { AlertsScreen } from "@/alerts-screen/alerts-screen";
import { useTelegramSettings } from "@/alerts-screen/use-telegram-settings";
import { ConnectionStatusGroup } from "@/connection-status";
import { DensityScreen } from "@/density/density-screen";
import type { ScreenMode } from "@/density/derive";
import { DisplayMinimumsPanel } from "@/density/display-minimums-panel";
import type { StreamState } from "@/density/stream-state";
import type { StreamStore } from "@/density/use-density-stream";
import { useDisplayMinAge } from "@/density/use-display-min-age";
import { useDisplayMinimums } from "@/density/use-display-minimums";
import { useHiddenCoins } from "@/density/use-hidden-coins";
import { useStreamSelector } from "@/density/use-stream-selector";
import type { ConnectionStatus } from "@/hooks/use-ipc-connection";
import {
  hashOfScreen,
  type Screen,
  shownDensityView,
  useDensityView,
  useSettingsReason,
} from "@/hooks/use-screen";
import { useSidebarOpen } from "@/hooks/use-sidebar-open";
import { InvitesDialog } from "@/invites/invites-dialog";
import type { Access, PlanOffer } from "@/lib/auth-api";
import { DISPLAY_MINIMUMS_MENU_LABEL } from "@/lib/labels";
import { visibleTier } from "@/lib/plan-gate";
import { PlanGateProvider } from "@/lib/plan-gate-context";
import { LibraryScreen } from "@/library/library-screen";
import { MarketActivityStatus } from "@/market-activity/market-activity-status";
import {
  buildNavItems,
  DENSITY_VIEW_LABELS,
  type NavGroup,
  type NavItem,
  SCREEN_LABELS,
} from "@/nav-items";
import { NavUser, type NavUserProps } from "@/nav-user";
import { SettingsScreen } from "@/settings/settings-screen";
import type { LiveMarket } from "@/terminal/terminal-settings-draft";
import { UnavailableMarketsBanner } from "@/unavailable-markets-banner";

export interface AppShellProps {
  login: string;
  /** Пользователю можно показать «Пригласить»: есть право и демон считает `signup-invite` включённым. */
  canInvite: boolean;
  /** Учётная запись владельца: в «Настройках» удаление не предлагается (§130 п. 5). */
  isOwner: boolean | null;
  screen: Screen;
  themeChoice: ThemeChoice;
  onThemeChoose: (choice: ThemeChoice) => void;
  onLogout: () => void;
  /** Учётная запись удалена: тот же уход на форму входа, что у «Выйти», но без предупреждения о выходе. */
  onAccountDeleted: (attempt: number) => void;
  onAccountDeleteStart: () => number;
  onAccountDeleteFailed: (attempt: number) => boolean;
  onSessionInvalid: () => void;
  /** Хранилище потока: каркас его не читает, чтобы кадры потока не перерисовывали панель и шапку. */
  streamStore: StreamStore;
  connectionStatus: ConnectionStatus;
  connectionSinceMs: number;
  alerts: UseAlertActionsResult;
  /** Биржи потока — варианты фильтров формы правила и библиотеки. */
  exchanges: readonly string[];
  /** Доступ по тарифу из `me`: подпись на значке профиля и замки экранов. `null` — ещё не прочитан. */
  access: Access | null;
  /** Состав тарифов из `me` — текст «Доступно на тарифе …». */
  plans: readonly PlanOffer[];
}

/**
 * Каркас вошедшего пользователя (stage8-sidebar-layout, design.md решения
 * 1, 3–6): боковая панель с меню пользователя, верхняя полоса, три экрана,
 * страница «Настройки» (§127 п. 7) и окна поверх. `App` монтирует его только при действительной сессии,
 * поэтому хуки внутри — настройки Telegram, выбор панели — не стреляют до
 * входа.
 */
export function AppShell({
  login,
  canInvite,
  isOwner,
  screen,
  themeChoice,
  onThemeChoose,
  onLogout,
  onAccountDeleted,
  onAccountDeleteStart,
  onAccountDeleteFailed,
  onSessionInvalid,
  streamStore,
  connectionStatus,
  connectionSinceMs,
  alerts,
  exchanges,
  access,
  plans,
}: AppShellProps) {
  const [sidebarOpen, setSidebarOpen] = useSidebarOpen();
  // Окно приглашений §104 п. 6: читает список только открытым.
  const [invitesOpen, setInvitesOpen] = useState(false);
  // Живая область исхода копирования тикера в верхней полосе: каркас держит
  // узел, экран плотностей рендерит в него текст порталом (design.md
  // stage9-redesign-chrome, Д4).
  const [noticeSlot, setNoticeSlot] = useState<HTMLSpanElement | null>(null);
  // Контейнер панели: к нему привязано меню профиля, чтобы оно стояло на одном
  // месте и в развёрнутой, и в свёрнутой панели (stage9-nav-redesign, Д5).
  const [panel, setPanel] = useState<HTMLDivElement | null>(null);
  // `null` до первого ответа настроек — баннер и пометки строк «без бота»
  // не показываются, пока статус не известен (spec `web-alerts-screen`).
  const [telegramConfigured, setTelegramConfigured] = useState<boolean | null>(null);
  const telegram = useTelegramSettings({
    onSessionInvalid,
    onConfiguredChange: setTelegramConfigured,
  });
  // Один экземпляр на каркас: окно правит список, экран плотностей читает его
  // же, и правка сразу меняет отбор строк (design.md
  // stage8-personal-display-filters, Д6).
  const [displayMinimumsOpen, setDisplayMinimumsOpen] = useState(false);
  const displayMinimums = useDisplayMinimums({ onSessionInvalid });
  // Один экземпляр на каркас: кнопка полосы фильтров правит время до показа
  // §82, экран плотностей читает его же (design.md stage9-display-min-age, Д5).
  const displayMinAge = useDisplayMinAge({ onSessionInvalid });
  const { reload: reloadMinAge } = displayMinAge;
  const reloadDisplayMinAge = useCallback(() => void reloadMinAge(), [reloadMinAge]);
  // Стабильные ссылки: иначе мемоизированная полоса фильтров перерисовывалась бы
  // на каждый рендер оболочки.
  const openDisplayMinimums = useCallback(() => setDisplayMinimumsOpen(true), []);
  const { reload: reloadMinimums } = displayMinimums;
  const reloadDisplayMinimums = useCallback(() => void reloadMinimums(), [reloadMinimums]);
  // Один экземпляр на каркас: страница «Настройки» возвращает монеты, экран
  // плотностей читает тот же список и отбирает строки сразу (§75, design.md
  // stage9-coin-hide, Д6).
  const hiddenCoins = useHiddenCoins({ onSessionInvalid });
  const { reload: reloadHiddenCoins } = hiddenCoins;
  const reloadHiddenCoinsList = useCallback(() => void reloadHiddenCoins(), [reloadHiddenCoins]);
  // Источник рынков для таблицы кодов терминала на странице «Настройки»: экран
  // плотностей записывает сюда геттер при монтировании (design.md
  // stage9-settings-page, Д4); до записи — пустой список.
  const liveMarketsSource = useRef<() => readonly LiveMarket[]>(() => []);
  const getLiveMarkets = useCallback(() => liveMarketsSource.current(), []);
  const settingsReason = useSettingsReason();
  const openSettings = useCallback(() => {
    window.location.hash = hashOfScreen("settings");
  }, []);
  // Вид из адреса — единственный источник правды о виде: его читают ссылки
  // панели, подсветка и экран плотностей. `null` — открыт другой экран.
  const densityView = useDensityView();
  // Последний вид, виденный на экране плотностей: нужен только скрытому
  // `DensityScreen`, пока открыт другой экран, — иначе при каждом уходе на
  // «Алерты» он перерисовывался бы в карточки. Это не запоминание вида (§34
  // ТЗ): состояние живёт в памяти вкладки, ссылки панели, подсветка и разбор
  // адреса его не читают, и при любом переходе на экран плотностей вид задаёт
  // адрес. Правка во время рендера — по схеме React для производного состояния.
  const [lastDensityView, setLastDensityView] = useState<ScreenMode>(densityView ?? "cards");
  if (densityView !== null && densityView !== lastDensityView) {
    setLastDensityView(densityView);
  }
  const shownDensityViewMode = shownDensityView(densityView, lastDensityView);
  const navGroups = useMemo(() => buildNavItems(screen, densityView), [screen, densityView]);
  const screenTitle =
    screen === "densities" ? DENSITY_VIEW_LABELS[shownDensityViewMode] : SCREEN_LABELS[screen];

  return (
    <PlanGateProvider access={access} plans={plans}>
      <SidebarProvider
        open={sidebarOpen}
        onOpenChange={setSidebarOpen}
        className="h-dvh min-h-0"
        // Размеры панели — решение экрана, а не примитива (stage9-nav-redesign,
        // Д1, §111 п. 1): 224 px развёрнута, 52 px полоса значков, переход 120 мс.
        style={
          {
            "--sidebar-width": "224px",
            "--sidebar-width-icon": "52px",
            "--sidebar-duration": "120ms",
          } as CSSProperties
        }
      >
        <Sidebar collapsible="icon" ref={setPanel} className="border-border">
          {/* Фон панели — `background` в обоих видах и на узком листе (макет):
            примитив красит себя в `sidebar`, поэтому закрашивает оболочка
            внутри него. Правая рамка — у контейнера примитива и у листа. */}
          <div className="flex size-full flex-col bg-background">
            {/* Шапка панели 52 px — вровень с верхней полосой экрана (Д9). */}
            <SidebarHeader className="h-13 shrink-0 flex-row items-center gap-2.5 p-0 px-4 group-data-[collapsible=icon]:px-[15px]">
              <SidebarBrand />
            </SidebarHeader>
            <SidebarContent>
              <nav aria-label="Экраны" className="flex flex-col gap-3.5 px-2 pt-1.5">
                <SidebarNavGroup key={navGroups.views.key} group={navGroups.views} />
                <SidebarNavGroup key={navGroups.screens.key} group={navGroups.screens} separated />
              </nav>
            </SidebarContent>
            <SidebarFooter className="flex-row items-center gap-1 border-t border-border p-2 group-data-[collapsible=icon]:flex-col">
              <SidebarFooterItems
                userProps={{
                  login,
                  canInvite,
                  onOpenInvites: () => setInvitesOpen(true),
                  themeChoice,
                  onThemeChoose,
                  onOpenSettings: openSettings,
                  onLogout,
                  menuAnchor: panel,
                  tier: visibleTier(access),
                }}
              />
            </SidebarFooter>
          </div>
          {/* Рельса на краю панели нет: §56 п. 1 называет два способа
            свернуть панель — кнопку и Cmd/Ctrl+B. */}
        </Sidebar>
        <SidebarInset className="min-w-0">
          {/* Верхняя полоса §88 п. 7 — одна на все экраны: название, связь,
            число инструментов, индикатор алертов справа. Высота 52 px — как у
            шапки панели (§111 п. 2). */}
          <header className="flex h-13 shrink-0 items-center gap-3.5 border-b border-border px-4">
            <MobileSidebarToggle />
            <h1 className="shrink-0 text-[15px] leading-none font-semibold tracking-[-0.005em]">
              {screenTitle}
            </h1>
            <span aria-hidden="true" className="h-3.5 w-px shrink-0 bg-border" />
            <ConnectionStatusGroup
              store={streamStore}
              connectionStatus={connectionStatus}
              connectionSinceMs={connectionSinceMs}
              noticeSlotRef={setNoticeSlot}
            />
            <div className="ml-auto flex shrink-0 items-center gap-3">
              <MarketActivityStatus store={streamStore} />
              <AlertStatus
                soundBlocked={alerts.soundBlocked}
                permission={alerts.permission}
                hasSoundRules={alerts.hasSoundRules}
                hasNotifyRules={alerts.hasNotifyRules}
                onRequestPermission={alerts.requestPermission}
                onUnlockSound={alerts.unlockSound}
              />
            </div>
          </header>
          {/* Недоступные рынки (§132 п. 3): одна плашка под полосой на все экраны. */}
          <UnavailableMarketsBanner store={streamStore} />
          {/* Все три экрана смонтированы всегда — переход не рвёт поток и не
            сбрасывает состояние DensityScreen (web-navigation, «Переход между
            экранами не рвёт поток и сигналы»). Неактивный скрыт атрибутом
            `hidden`; класс `flex` ставится только на видимую обёртку, иначе он
            перебивает `[hidden] { display: none }` той же специфичностью. */}
          <div
            hidden={screen !== "densities"}
            className={cn("min-h-0 flex-1 flex-col", screen === "densities" && "flex")}
          >
            <DensityScreen
              store={streamStore}
              active={screen === "densities"}
              connectionStatus={connectionStatus}
              onSessionInvalid={onSessionInvalid}
              displayMinimums={displayMinimums.state}
              onOpenDisplayMinimums={openDisplayMinimums}
              onPutDisplayMinimum={displayMinimums.put}
              onRemoveDisplayMinimum={displayMinimums.remove}
              onReloadDisplayMinimums={reloadDisplayMinimums}
              hiddenCoins={hiddenCoins.state}
              onHideCoin={hiddenCoins.hide}
              pendingHiddenBases={hiddenCoins.pendingBases}
              displayMinAge={displayMinAge.state}
              onSaveDisplayMinAge={displayMinAge.save}
              onRemoveDisplayMinAge={displayMinAge.remove}
              onReloadDisplayMinAge={reloadDisplayMinAge}
              mode={shownDensityViewMode}
              noticeSlot={noticeSlot}
              liveMarketsSource={liveMarketsSource}
            />
          </div>
          <section
            hidden={screen !== "alerts"}
            aria-label="Алерты"
            className="min-h-0 flex-1 overflow-auto"
          >
            <AlertsScreen
              enabled
              active={screen === "alerts"}
              exchanges={exchanges}
              telegramConfigured={telegramConfigured}
              onSessionInvalid={onSessionInvalid}
              onRulesChanged={alerts.refresh}
            />
          </section>
          <section
            hidden={screen !== "library"}
            aria-label="Библиотека"
            className="min-h-0 flex-1 overflow-auto"
          >
            <LibraryScreen
              active={screen === "library"}
              exchanges={exchanges}
              onSessionInvalid={onSessionInvalid}
            />
          </section>
          {/* Страница настроек монтируется только пока открыта: черновики
            Telegram и терминала живут в её состоянии и уходят вместе с ней
            (design.md stage9-settings-page, Д2). */}
          {screen === "settings" ? (
            <section aria-label="Настройки" className="min-h-0 flex-1 overflow-auto">
              <SettingsScreen
                reason={settingsReason}
                telegram={telegram}
                hiddenCoins={hiddenCoins.state}
                pendingHiddenBases={hiddenCoins.pendingBases}
                onUnhideCoin={hiddenCoins.unhide}
                onReloadHiddenCoins={reloadHiddenCoinsList}
                getLiveMarkets={getLiveMarkets}
                isOwner={isOwner}
                onAccountDeleted={onAccountDeleted}
                onAccountDeleteStart={onAccountDeleteStart}
                onAccountDeleteFailed={onAccountDeleteFailed}
                onSessionInvalid={onSessionInvalid}
              />
            </section>
          ) : null}
        </SidebarInset>
        {canInvite ? (
          <InvitesDialog
            open={invitesOpen}
            onOpenChange={setInvitesOpen}
            onSessionInvalid={onSessionInvalid}
          />
        ) : null}
        <Sheet open={displayMinimumsOpen} onOpenChange={setDisplayMinimumsOpen}>
          <SheetContent className="flex data-[side=right]:w-[min(460px,92vw)] flex-col p-0 data-[side=right]:sm:max-w-none">
            <SheetHeader className="border-b border-border pr-12">
              <SheetTitle>{DISPLAY_MINIMUMS_MENU_LABEL}</SheetTitle>
            </SheetHeader>
            <div className="min-h-0 flex-1 overflow-auto p-4">
              <ConnectedDisplayMinimumsPanel
                store={streamStore}
                state={displayMinimums.state}
                onPut={displayMinimums.put}
                onRemove={displayMinimums.remove}
                onReload={() => void displayMinimums.reload()}
              />
            </div>
          </SheetContent>
        </Sheet>
      </SidebarProvider>
    </PlanGateProvider>
  );
}

/** Окно минимумов берёт инструменты потока селектором: каркас состояние потока не читает. */
function selectInstruments(state: StreamState): StreamState["instruments"] {
  return state.instruments;
}

function ConnectedDisplayMinimumsPanel({
  store,
  ...props
}: { store: StreamStore } & Omit<ComponentProps<typeof DisplayMinimumsPanel>, "instruments">) {
  const instruments = useStreamSelector(store, selectInstruments);
  return <DisplayMinimumsPanel {...props} instruments={instruments} />;
}

/**
 * Знак продукта в шапке панели (Д8): бруски 22×20 и слово «kelv» текстом;
 * в свёрнутой панели слова нет. Анимируется только знак.
 */
function SidebarBrand() {
  return (
    <>
      <Logo variant="mark" animated className="h-5 w-[22px] shrink-0" />
      <span
        aria-hidden="true"
        className="text-[17px] leading-none font-semibold tracking-[-0.005em] group-data-[collapsible=icon]:hidden"
      >
        kelv
      </span>
    </>
  );
}

/**
 * Группа пунктов с подписью (Д2). Подпись — заголовок списка для читающих с
 * экрана, а не ссылка; в свёрнутой панели её нет. Перед второй группой
 * свёрнутая панель рисует линию вместо подписи (§111 п. 3).
 */
function SidebarNavGroup({ group, separated = false }: { group: NavGroup; separated?: boolean }) {
  const labelId = useId();

  return (
    <SidebarGroup className="gap-0.5 p-0">
      {separated ? (
        <SidebarSeparator className="mx-2.5 mb-2.5 hidden bg-border group-data-[collapsible=icon]:block" />
      ) : null}
      <SidebarGroupLabel
        id={labelId}
        className="h-[22px] px-2 text-[11px] font-medium tracking-[.06em] text-muted-foreground uppercase group-data-[collapsible=icon]:hidden"
      >
        {group.label}
      </SidebarGroupLabel>
      <SidebarMenu
        aria-labelledby={labelId}
        className="gap-0.5 group-data-[collapsible=icon]:items-center"
      >
        {group.items.map((item) => (
          <SidebarNavItem key={item.key} item={item} />
        ))}
      </SidebarMenu>
    </SidebarGroup>
  );
}

/**
 * Низ панели: профиль и кнопка сворачивания. Порядок в DOM равен видимому,
 * чтобы Tab шёл по картинке: в свёрнутой панели кнопка разворота над
 * аватаром, в развёрнутой профиль слева от неё. Кнопка при переключении
 * размонтируется на одном месте и монтируется на другом, поэтому футер
 * передаёт фокус новой кнопке (флаг `focusHandoff`).
 */
function SidebarFooterItems({ userProps }: { userProps: NavUserProps }) {
  const { open } = useSidebar();
  const focusHandoff = useRef(false);

  return (
    <>
      {open ? null : <SidebarRailToggle focusHandoff={focusHandoff} />}
      <SidebarMenu className="min-w-0 flex-1 group-data-[collapsible=icon]:flex-none group-data-[collapsible=icon]:items-center">
        <NavUser {...userProps} />
      </SidebarMenu>
      {open ? <SidebarRailToggle focusHandoff={focusHandoff} /> : null}
    </>
  );
}

/**
 * Кнопка сворачивания внизу панели (§56 п. 1 в редакции 1.75, §111 п. 5):
 * справа от кнопки профиля, в свёрнутой панели — над аватаром. На узком
 * экране панель — выезжающий лист, и кнопка живёт в верхней полосе
 * (`MobileSidebarToggle`), а здесь не рисуется.
 */
function SidebarRailToggle({ focusHandoff }: { focusHandoff: RefObject<boolean> }) {
  const { isMobile, open, toggleSidebar } = useSidebar();

  // Ref-функция, а не эффект: она срабатывает на каждый новый узел кнопки,
  // в том числе после перехода узкий -> широкий вид, когда компонент жив, а
  // узла ещё не было. Уходя, кнопка записывает, был ли в ней фокус (узел ещё
  // в документе); пришедшая на её место забирает фокус при монтировании.
  // Флаг гасится сразу после коммита, чтобы уход кнопки без замены (узкий
  // вид) не отдал фокус случайной кнопке позже.
  const attachButton = useCallback(
    (element: HTMLButtonElement | null) => {
      if (element === null) {
        return;
      }
      if (focusHandoff.current) {
        focusHandoff.current = false;
        element.focus();
      }
      return () => {
        if (document.activeElement === element) {
          focusHandoff.current = true;
          queueMicrotask(() => {
            focusHandoff.current = false;
          });
        }
      };
    },
    [focusHandoff],
  );

  if (isMobile) {
    return null;
  }
  // Имя кнопки постоянное, состояние сообщает `aria-expanded`: меняющееся
  // имя вдобавок к нему читалось бы дважды. Действие — в подсказке.
  const hint = open ? "Свернуть" : "Развернуть";

  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <Button
            ref={attachButton}
            variant="ghost"
            size="icon"
            data-slot="sidebar-rail-toggle"
            aria-label="Свернуть или развернуть панель"
            aria-expanded={open}
            onClick={toggleSidebar}
            // `transition-colors`, а не `transition-all` кнопки: иначе плавно менялся бы и
            // `order`, и значок на 150 мс оставался бы слева от профиля.
            className="size-8 shrink-0 rounded-md text-muted-foreground transition-colors hover:bg-foreground/5 hover:text-foreground aria-expanded:bg-transparent aria-expanded:text-muted-foreground aria-expanded:hover:bg-foreground/5 aria-expanded:hover:text-foreground"
          />
        }
      >
        {open ? (
          <RiSidebarFoldLine aria-hidden="true" className="size-4" />
        ) : (
          <RiSidebarUnfoldLine aria-hidden="true" className="size-4" />
        )}
      </TooltipTrigger>
      <TooltipContent side="right">{hint} · Cmd/Ctrl+B</TooltipContent>
    </Tooltip>
  );
}

/**
 * Кнопка панели в верхней полосе — только на узком экране, где панели нет и
 * открыть её больше нечем. Сообщает читающим с экрана, выехал ли лист.
 */
function MobileSidebarToggle() {
  const { isMobile, openMobile } = useSidebar();
  if (!isMobile) {
    return null;
  }

  return (
    <SidebarTrigger
      className="-ml-1 shrink-0"
      aria-label="Свернуть или развернуть панель"
      aria-expanded={openMobile}
    />
  );
}

/**
 * Пункт панели (Д3, §111 п. 4): высота 32 px, скругление 8 px. Развёрнут —
 * значок 16 px и подпись 13 px; свёрнут — только значок по центру в ячейке
 * шириной 36 px, подпись остаётся текстом для читающих с экрана, а
 * видимой становится подсказкой справа от значка.
 *
 * Текущий пункт отмечен не только цветом (web-navigation): подложкой `accent`,
 * основным цветом и полужирной подписью. Прежняя полоса у края убрана. Вариант
 * `data-active` повторён рядом с `hover`: при равной специфичности наведение
 * перебило бы подложку текущего.
 */
const NAV_ITEM_CLASS =
  "h-8 gap-2.5 rounded-md px-2 py-0 text-[13px] font-normal text-muted-foreground ring-ring hover:bg-foreground/5 hover:text-foreground focus-visible:ring-2 data-active:bg-accent data-active:font-medium data-active:text-foreground data-active:before:hidden data-active:hover:bg-accent group-data-[collapsible=icon]:h-8! group-data-[collapsible=icon]:w-9! group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:p-0! group-data-[collapsible=icon]:[&>span]:sr-only";

function SidebarNavItem({ item }: { item: NavItem }) {
  const { isMobile, setOpenMobile } = useSidebar();

  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        isActive={item.current}
        tooltip={{ children: item.label, sideOffset: 10 }}
        className={NAV_ITEM_CLASS}
        render={
          <a
            href={item.href}
            aria-current={item.current ? "page" : undefined}
            // На узком экране панель выезжает поверх и закрывается выбором пункта.
            onClick={isMobile ? () => setOpenMobile(false) : undefined}
          />
        }
      >
        {item.icon}
        <span>{item.label}</span>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}
